'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';

import { SIGN_IN_DIALOG } from '@eliadmin/core/src/components/dialog/lazy-dialogs';
import Link from '@eliadmin/core/src/components/link';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';

import { AuthDialogContent } from '../auth/sign-in/auth-dialog';
import { FORGOT_LINK, SignInForm } from '../auth/sign-in/sign-in-form';
import { useSignInForm } from '../auth/sign-in/use-sign-in-form';

/**
 * Sign-in that keeps the customer on the page (cart, checkout, reviews, guest favourites): the
 * NajTonery sign-in form (Figma 221:13176) in the modal of the reset flow (223:14538), rendered in
 * the core dialog slot. The sign-in is the core one without the redirect; the dialog closes once
 * the customer is known. Only core words here – the dialog slot sits outside the page, so the admin
 * texts of the sign-in page are not available: „Prihláste sa“, „Zabudol som heslo“ (the core cart
 * form's link, to the reset page).
 */
export function StaySignInDialog() {
  const { t } = useTranslations();
  const me = useCustomerStore(state => state.me);
  const close = useDialogStore(state => state.close);
  const pathname = usePathname();
  const search = useSearchParams().toString();
  const state = useSignInForm({ redirectTo: null });
  const titleRef = useRef<HTMLHeadingElement>(null);
  const opener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (me) close(SIGN_IN_DIALOG);
  }, [close, me]);

  return (
    <AuthDialogContent
      closeLabel={t('text272')}
      aria-describedby={undefined}
      onOpenAutoFocus={event => {
        event.preventDefault();
        // The core slot has no Radix trigger: remember the control that opened the dialog.
        if (document.activeElement instanceof HTMLElement) opener.current = document.activeElement;
        titleRef.current?.focus();
      }}
      onCloseAutoFocus={event => {
        event.preventDefault();
        if (opener.current?.isConnected) opener.current.focus();
      }}
    >
      <DialogPrimitive.Title
        ref={titleRef}
        tabIndex={-1}
        className="nt-h4 text-center text-[#0D110D] outline-none"
      >
        {t('text357')}
      </DialogPrimitive.Title>
      <SignInForm
        id="stay-sign-in"
        state={state}
        socialReturnUrl={search ? `${pathname}?${search}` : pathname}
        forgot={
          <Link href={ROUTES.PASSWORD_RESET() ?? '#'} prefetch={false} className={FORGOT_LINK}>
            {t('text622')}
          </Link>
        }
      />
    </AuthDialogContent>
  );
}

StaySignInDialog.id = SIGN_IN_DIALOG;

/** Opens `StaySignInDialog` (same slot id as the core sign-in dialog). */
export function useOpenStaySignIn() {
  const open = useDialogStore(state => state.open);
  return () => open({ id: SIGN_IN_DIALOG, content: <StaySignInDialog /> });
}
